/**
 * Zustand store for managing optimization progress state.
 */

import { create } from 'zustand';

interface OptimizationProgress {
  current: number;
  total: number;
  percent: number;
}

interface OptimizationStore {
  // State
  isOptimizing: boolean;
  optimizationProgress: OptimizationProgress | null;
  optimizationError: string | null;
  usingOptimizedParams: boolean;

  // Actions
  setOptimizing: (optimizing: boolean) => void;
  setProgress: (progress: OptimizationProgress | null) => void;
  setOptimizationError: (error: string | null) => void;
  setUsingOptimizedParams: (using: boolean) => void;
  clearOptimization: () => void;
  /**
   * Full reset to the default state, including `usingOptimizedParams`
   * (which `clearOptimization` deliberately leaves untouched). Called on
   * logout/login (SHA-71) so a previous account's optimized-params flag
   * doesn't leak into the next account's session.
   */
  reset: () => void;
}

export const useOptimizationStore = create<OptimizationStore>((set) => ({
  // Initial state
  isOptimizing: false,
  optimizationProgress: null,
  optimizationError: null,
  usingOptimizedParams: false,

  // Actions
  setOptimizing: (optimizing) =>
    set({
      isOptimizing: optimizing,
      optimizationError: optimizing ? null : undefined, // Clear error when starting
    }),

  setProgress: (progress) =>
    set({
      optimizationProgress: progress,
    }),

  setOptimizationError: (error) =>
    set({
      optimizationError: error,
      isOptimizing: false,
      optimizationProgress: null,
    }),

  setUsingOptimizedParams: (using) =>
    set({
      usingOptimizedParams: using,
    }),

  clearOptimization: () =>
    set({
      isOptimizing: false,
      optimizationProgress: null,
      optimizationError: null,
    }),

  reset: () =>
    set({
      isOptimizing: false,
      optimizationProgress: null,
      optimizationError: null,
      usingOptimizedParams: false,
    }),
}));
